import { useState, useEffect } from "react";
import { Save } from "lucide-react";
import {
  configuracaoIntegracaoSisodontoService,
  tipoCaixaService,
  usuarioService,
} from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import PageContainer from "../../components/PageContainer";
import { TipoCaixaSelect } from "../../components/ui";
import RoboLocalSisodonto from "./RoboLocalSisodonto";

const formVazio = {
  usuarioId: "",
  tipoCaixaFinanceiroId: "",
  tipoCaixaProteseId: "",
  tipoCaixaOrtodontiaId: "",
};

const ConfiguracaoCaixaSisodonto = () => {
  const [tiposCaixa, setTiposCaixa] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(formVazio);
  const [configuracaoCarregada, setConfiguracaoCarregada] = useState(false);
  const { showError, showSuccess } = useNotification();

  useEffect(() => {
    loadTiposCaixa();
    loadUsuarios();
    loadConfiguracao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTiposCaixa = async () => {
    try {
      const data = await tipoCaixaService.listarTipoCaixa();
      setTiposCaixa(data);
    } catch (err) {
      setTiposCaixa([]);
    }
  };

  const loadUsuarios = async () => {
    try {
      const data = await usuarioService.listarUsuariosAtivos();
      setUsuarios(data);
    } catch (err) {
      setUsuarios([]);
    }
  };

  const loadConfiguracao = async () => {
    setLoading(true);
    try {
      const data = await configuracaoIntegracaoSisodontoService.buscar();
      setFormData({
        usuarioId: data?.usuario?.id || "",
        tipoCaixaFinanceiroId: data?.tipoCaixaFinanceiro?.id || "",
        tipoCaixaProteseId: data?.tipoCaixaProtese?.id || "",
        tipoCaixaOrtodontiaId: data?.tipoCaixaOrtodontia?.id || "",
      });
    } catch (err) {
      showError(err.message || "Erro ao carregar configuração da integração Sisodonto");
    } finally {
      setLoading(false);
      setConfiguracaoCarregada(true);
    }
  };

  // O usuário responsável é obrigatório: é o dono de todos os caixas criados
  // pela integração (sem ele, dois admins poderiam criar o mesmo dia duas vezes).
  const usuarioResponsavelAusente = !formData.usuarioId;
  const destacarUsuarioAusente =
    configuracaoCarregada && usuarioResponsavelAusente;

  const handleSubmit = async () => {
    if (usuarioResponsavelAusente) {
      showError("Selecione o usuário responsável pelos caixas.");
      return;
    }

    const payload = {
      usuario: { id: formData.usuarioId },
      ...(formData.tipoCaixaFinanceiroId
        ? { tipoCaixaFinanceiro: { id: formData.tipoCaixaFinanceiroId } }
        : {}),
      ...(formData.tipoCaixaProteseId
        ? { tipoCaixaProtese: { id: formData.tipoCaixaProteseId } }
        : {}),
      ...(formData.tipoCaixaOrtodontiaId
        ? { tipoCaixaOrtodontia: { id: formData.tipoCaixaOrtodontiaId } }
        : {}),
    };

    setSaving(true);
    try {
      await configuracaoIntegracaoSisodontoService.atualizar(payload);
      showSuccess("Configuração da integração Sisodonto salva com sucesso!");
      await loadConfiguracao();
    } catch (err) {
      showError(err.message || "Erro ao salvar configuração");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Configuração Integração Sisodonto
        </h1>
        <p className="text-gray-600">
          Configuração única desta organização para a integração automática
          com o Sisodonto: qual tipo de caixa recebe o valor de cada módulo
          (financeiro, prótese, ortodontia) e qual usuário fica dono dos
          caixas criados.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow border p-6">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando...</div>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="tipo-caixa-financeiro"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Tipo de Caixa — Financeiro
              </label>
              <TipoCaixaSelect
                id="tipo-caixa-financeiro"
                tiposCaixa={tiposCaixa}
                value={formData.tipoCaixaFinanceiroId}
                onChange={(e) =>
                  setFormData({ ...formData, tipoCaixaFinanceiroId: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="tipo-caixa-protese"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Tipo de Caixa — Prótese
              </label>
              <TipoCaixaSelect
                id="tipo-caixa-protese"
                tiposCaixa={tiposCaixa}
                value={formData.tipoCaixaProteseId}
                onChange={(e) =>
                  setFormData({ ...formData, tipoCaixaProteseId: e.target.value })
                }
              />
            </div>

            <div>
              <label
                htmlFor="tipo-caixa-ortodontia"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Tipo de Caixa — Ortodontia
              </label>
              <TipoCaixaSelect
                id="tipo-caixa-ortodontia"
                tiposCaixa={tiposCaixa}
                value={formData.tipoCaixaOrtodontiaId}
                onChange={(e) =>
                  setFormData({ ...formData, tipoCaixaOrtodontiaId: e.target.value })
                }
              />
            </div>

            <div className="pt-2 border-t">
              <label
                htmlFor="usuario-responsavel"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Usuário responsável pelos caixas (obrigatório)
              </label>
              <select
                id="usuario-responsavel"
                required
                aria-invalid={destacarUsuarioAusente}
                value={formData.usuarioId}
                onChange={(e) =>
                  setFormData({ ...formData, usuarioId: e.target.value })
                }
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  destacarUsuarioAusente
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                }`}
              >
                <option value="" disabled>
                  Selecione um usuário
                </option>
                {usuarios.map((usuario) => (
                  <option key={usuario.id} value={usuario.id}>
                    {usuario.nome}
                  </option>
                ))}
              </select>
              {destacarUsuarioAusente && (
                <p role="alert" className="mt-1 text-xs font-medium text-red-600">
                  Defina o usuário responsável para poder integrar com o
                  Sisodonto.
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Todos os caixas criados pela integração ficam no nome desse
                usuário, e não de quem clicou no botão. O ideal é um usuário
                dedicado (ex.: “Robô Sisodonto”) usado só como dono dos caixas.
              </p>
            </div>

            <div className="pt-2">
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save size={16} />
                {saving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        )}
      </div>

      <RoboLocalSisodonto />
    </PageContainer>
  );
};

export default ConfiguracaoCaixaSisodonto;
